"use client";

import React, { useState } from 'react';
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Star, Send } from 'lucide-react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"; // FIXED: Import from form.tsx
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from 'react-hook-form';
import * as z from "zod";
import { showSuccess, showError } from '@/utils/toast';

const schema = z.object({
  score: z.number().min(1, { message: "Score must be between 1 and 10" }).max(10, { message: "Score must be between 1 and 10" }),
  feedback: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface ClientNPSProps {
  onComplete?: () => void;
}

export const ClientNPS: React.FC<ClientNPSProps> = ({ onComplete }: ClientNPSProps) => {
  const { profile } = useAuth();
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      score: 0,
      feedback: '',
    },
  });

  const handleSubmit = async (data: FormData) => {
    if (!profile) return;

    try {
      const { error } = await supabase
        .from('nps_responses')
        .insert({
          user_id: profile.id,
          company_id: profile.company_id,
          score: data.score,
          feedback: data.feedback,
          submitted_at: new Date().toISOString(),
        });

      if (error) throw error;

      showSuccess('Thank you for your feedback!');
      form.reset();
      onComplete?.();
    } catch (error: any) {
      showError('Failed to submit feedback');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="score"
          render={({ field }) => (
            <FormItem>
              <FormLabel>How likely are you to recommend WightWay Connect to a colleague? (1-10)</FormLabel>
              <FormControl>
                <div className="flex justify-center space-x-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <Button
                      key={num}
                      type="button"
                      variant={field.value === num ? 'default' : 'outline'}
                      onClick={() => field.onChange(num)}
                      className="w-10 h-10 rounded-full"
                    >
                      {num}
                    </Button>
                  ))}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="feedback"
          render={({ field }) => (
            <FormItem>
              <FormLabel>What can we improve? (Optional)</FormLabel>
              <FormControl>
                <Input
                  id="feedback"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Your thoughts..."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full" disabled={form.formState.isSubmitting || form.watch('score') === 0}>
          <Send className="mr-2 h-4 w-4" />
          {form.formState.isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </form>
    </Form>
  );
};